import React, { useState, useEffect, useMemo } from 'react'
import Button from '@mui/material/Button'
import { useTranslate, Form, Create, useNotify, useRemoveFromStore, useStore, useRefresh } from 'react-admin'
import { Grid, Skeleton } from '@mui/material'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { getIdsfromShortName } from 'src/helper/getIdsThroughData'
import { TransferList } from 'src/components/uiElements/fields/TransferList'
import { userServices } from 'src/services/auth/user/user.service'
import { permissionServices } from 'src/services/auth/permission/permission.service'
import { FieldsByApplicationEdit } from 'src/components/pages/authentication/user/logic/edit/FieldsByApplicationEdit'
import LoadingButton from '@mui/lab/LoadingButton'
import SaveIcon from '@mui/icons-material/Save'
import { customAwait } from 'src/components/pages/sales/reservation/helper/customAwait'
import { JsonInput } from 'src/components/uiElements/react-hook-fields/JsonInputReact'

export const ModalEditApp = ({
  handleClose,
  open,
  userByClient_id,
  application_id,
  client_id,
  dashboard,
  config,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const remove = useRemoveFromStore()
  const refresh = useRefresh()
  const [hashuser] = useStore('hash_user_edit')
  const [loadingData, setLoadingData] = useState(true)
  const [clients, setClients] = useState([])
  const [applications, setApplications] = useState([])
  const [applicationConfig, setApplicationConfig] = useState()
  const [roles, setRoles] = useState([])
  const [selectedHashClient, setSelectedHashClient] = useState()
  const [checkedRoles, setCheckedRoles] = useState([])
  const [loadingPermissions, setLoadingPermissions] = useState(true)
  const [permissionNotGranted, setPermissionNotGranted] = useState([])
  const [permissionGranted, setPermissionGranted] = useState([])
  const [workUnitBusinessUnit, setWorkUnitBusinessUnit] = useState({
    work_unit_id: [],
    business_unit_id: [],
  })
  const [loading, setLoading] = useState(false)

  const dashboardsOptions = useMemo(() => ([
    'SELLER',
    'OPERATIONS',
    ...(applicationConfig?.dashboard?.options ?? [])
  ]), applicationConfig?.dashboard)

  useEffect(() => {
    ;(async () => {
      if (open && userByClient_id) {
        setLoadingData(true)
        try {
          const userByClientApp = await userServices.getByIduserByClientApp(userByClient_id)
          setSelectedHashClient(userByClientApp.application_by_client.client.hash_client)
          setClients([userByClientApp.application_by_client.client])
          setApplications([userByClientApp.application_by_client.application])
          setRoles(userByClientApp.roles)
          setCheckedRoles(getIdsfromShortName(userByClientApp.roles, userByClientApp.rolesSelectedName))
          setApplicationConfig(
            userByClientApp.application_by_client?.config ||
              userByClientApp.application_by_client?.application?.config
          )
          setLoadingData(false)
        } catch (error) {
          resetForm()
        }
      } else {
        resetForm()
      }
    })()
  }, [open, userByClient_id])

  const getPermissionName = (permission) => {
    let text = [
      permission.id,
      permission.description
    ]

    if (permission?.entity_relation?.name) {
      text.push(translate(`ra.entity.${permission?.entity_relation?.name}`))
    }

    if (permission?.action) {
      text.push(translate(`ra.action.${permission?.action}`))
    }

    return text.join(' - ')
  }

  useEffect(() => {
    ;(async () => {
      if (userByClient_id && application_id) {
        setLoadingPermissions(true)
        try {
          const permissionUserByClientApp = await permissionServices.permissionUserByClientApp(
            application_id,
            userByClient_id
          )
          if (permissionUserByClientApp.permissionGranted && permissionUserByClientApp.permissionNotGranted) {
            const { permissionNotGranted, permissionGranted } = permissionUserByClientApp
            setPermissionNotGranted(permissionNotGranted.map(permission => ({
              ...permission,
              showField: getPermissionName(permission)
            })))
            setPermissionGranted(permissionGranted.map(permission => ({
              ...permission,
              showField: getPermissionName(permission)
            })))
          } else {
            setPermissionNotGranted([])
            setPermissionGranted([])
          }
          setLoadingPermissions(false)
        } catch (error) {
          setLoadingPermissions(false)
        }
      } else {
        setPermissionNotGranted([])
        setPermissionGranted([])
        setLoadingPermissions(false)
      }
    })()
  }, [userByClient_id, application_id])

  const resetForm = () => {
    setClients([])
    setApplications([])
    setCheckedRoles([])
    setRoles([])
    setApplicationConfig({})
    setLoadingData(false)
  }

  const transformData = (checkedRoles, permission, workUnitAndBusinessUnit, dashboard, config) => {
    const permission_id = permission.map((item) => item.id)
    return {
      role_id: checkedRoles,
      permission_id,
      field_application: {...workUnitAndBusinessUnit, hash_client:selectedHashClient},
      hashuser,
      endpoint: applicationConfig?.reactAdmin?.components?.user?.edit?.endpoint,
      dashboard,
      config,
    }
  }

  const postSave = async (bodyForm) => {
    const formattedWorkUnitBusinessUnit = {
      ...workUnitBusinessUnit,
      business_unit_id:workUnitBusinessUnit.business_unit_id?.filter(bu=>bu)
    }
    const body = transformData(
      checkedRoles,
      permissionGranted,
      formattedWorkUnitBusinessUnit,
      bodyForm?.dashboard,
      bodyForm?.config
    )
    try {
      setLoading(true)
      await userServices.putUserByClientApp(body, userByClient_id)
      notify('ra.notification.updated_success_femenine', {
        type: 'success',
        messageArgs: { element: translate('ra.single_entity.application') },
      })
      setLoading(false)
      onClickClose()
    } catch (error) {
      setLoading(false)
      notify('ra.notification.updated_error_femenine', {
        type: 'success',
        messageArgs: { element: translate('ra.single_entity.application') },
      })
    } finally {
      customAwait([refresh])
    }
  }

  const onClickClose = () => {
    remove('hash_user_edit')
    handleClose()
  }

  const handleChangePermissions = (left, right) => {
    setPermissionNotGranted(left)
    setPermissionGranted(right)
  }

  return (
    <div>
      <Dialog open={open} onClose={onClickClose} maxWidth='false' fullWidth={true}>
        <DialogTitle>{translate('ra.action.edit')}</DialogTitle>
        <Create>
          <Form onSubmit={postSave}>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4} className='uiInputLabel'>
                  <EditInputsWrap label={translate('ra.field.client')} md={12}>
                    {loadingData ? (
                      <Skeleton variant='rectangular' width={'100%'} height={40} />
                    ) : (
                      <SelectInput
                        source='client'
                        options={clients}
                        optionsText={['long_name']}
                        disabled
                        defaultValue={client_id}
                        fullWidth
                      />
                    )}
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.application')} md={12}>
                    {loadingData ? (
                      <Skeleton variant='rectangular' width={'100%'} height={40} />
                    ) : (
                      <SelectInput
                        source='application'
                        options={applications}
                        optionsText={['long_name']}
                        disabled
                        defaultValue={application_id}
                        fullWidth
                      />
                    )}
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.dashboard')} md={12}>
                    <SelectInput
                      source='dashboard'
                      optionsText={['name']}
                      optionValue='id'
                      sx={{ width: 300 }}
                      options={dashboardsOptions.map(elem => ({
                        id: elem, name: translate(`ra.field.${elem.toLowerCase()}`)
                      }))}
                      defaultValue={dashboard}
                      label={null}
                    />
                  </EditInputsWrap>
                  <EditInputsWrap
                    label={translate('ra.field.json')}
                    md={12}
                    style={{ paddingBottom: '20px' }}
                  >
                    <JsonInput source={`config`} defaultValue={config} />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.role')} md={12}>
                    {loadingData ? (
                      <Skeleton variant='rectangular' width={'100%'} height={40} />
                    ) : (
                      <SelectInput
                        source='role'
                        options={roles}
                        defaultValue={checkedRoles}
                        onChange={(e) => setCheckedRoles(e.target.value)}
                        optionsText={['short_name', 'long_name']}
                        multiple
                        showSearch
                      />
                    )}
                  </EditInputsWrap>

                  {applicationConfig && (
                    <FieldsByApplicationEdit
                      workUnitBusinessUnit={workUnitBusinessUnit}
                      setWorkUnitBusinessUnit={setWorkUnitBusinessUnit}
                      config={applicationConfig}
                      loading={loadingPermissions}
                      hash_client={selectedHashClient}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={8} className='uiInputLabel'>
                  <EditInputsWrap
                    // tooltiptext={translate('ra.message.permission_tooltip')}
                    label={translate('ra.field.permission')}
                    md={12}
                  >
                    {loadingPermissions ? (
                      <Grid
                        container
                        spacing={2}
                        justifyContent='center'
                        alignItems='center'
                        className='transferListComponent'
                      >
                        <Grid item xs={5}>
                          <Skeleton variant='rectangular' width={'100%'} height={500} />
                        </Grid>
                        <Grid item xs={2}>
                          <Grid container direction='column' alignItems='center'>
                            <Skeleton variant='rectangular' width={'100%'} height={40} />
                          </Grid>
                        </Grid>
                        <Grid item xs={5}>
                          <Skeleton variant='rectangular' width={'100%'} height={500} />
                        </Grid>
                      </Grid>
                    ) : (
                      <TransferList
                        leftData={permissionNotGranted}
                        rightData={permissionGranted}
                        setter={handleChangePermissions}
                        labelField='showField'
                      />
                    )}
                  </EditInputsWrap>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className='dialog-actions'>
              <LoadingButton
                loading={loading || loadingData || loadingPermissions}
                loadingPosition='start'
                startIcon={<SaveIcon />}
                variant='contained'
                type='submit'
              >
                {translate('ra.action.save')}
              </LoadingButton>
              <Button onClick={onClickClose} variant='outlined' color='error'>
                {translate('ra.action.cancel')}
              </Button>
            </DialogActions>
          </Form>
        </Create>
      </Dialog>
    </div>
  )
}
